import { useBenefitDraftState, useBenefitSaving, useBenefitReviewActive } from "@/features/benefit-workspace/benefitEditorContext"
import { useBenefitTargetYear } from "@/features/benefit-workspace/useBenefitProgramReads"
import { IconCalendar } from "@tabler/icons-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { EditableCardActions } from "./field"
import { api } from "@/lib/api"
import { saveBenefitChange } from "@/lib/benefit-save"
import { useBenefitEdit } from "@/hooks/useBenefitEdit"
import { toast } from "sonner"
import { targetYearRecordReason, targetYearStatusLabel as statusLabel } from "@/lib/target-year-edit"
import type { BenefitDetailDto } from "@/lib/benefit-types"

/** The shared persisted picker used by the new-intake wizard and unified detail page. */
export function TargetProgramYearSection({ benefit, canEdit, onUpdated }: {
  benefit: BenefitDetailDto
  canEdit: boolean
  onUpdated: (updated: BenefitDetailDto) => void
}) {
  const { readback, loading, error } = useBenefitTargetYear(benefit)
  const [editing, setEditing, token] = useBenefitEdit(benefit.concurrencyToken, "target-year")
  const [pendingSave, setSaving] = useBenefitSaving()
  const saving = useBenefitReviewActive() || pendingSave
  const [selected, setSelected] = useBenefitDraftState("target-year", "selected", "default")
  const [choices, setChoices] = useBenefitDraftState("target-year", "choices", null)
  const selection = editing ? selected : readback?.selectedProgramYear == null ? "default" : String(readback.selectedProgramYear)

  // The parent supplies canEdit from the server-owned section matrix; the record predicate is
  // shared with the card that hosts this editor so a disabled pencil can explain itself.
  const editable = canEdit && targetYearRecordReason(benefit) === null
  const currentChoices = readback?.options.filter(option => selection === "default" ? option.isDefault : String(option.programYear) === selection) ?? []
  const choiceCurrent = currentChoices.length === 1 && currentChoices[0]!.isAvailable
  const canSave = editable && !loading && !error && !!readback && choiceCurrent

  const save = async () => {
    if (!editing || saving || !canSave) { toast.error("Reload target-year choices before saving."); return }
    if (!token) { toast.error("Reload before changing the target program year."); return }
    setSaving(true)
    const result = await saveBenefitChange(benefit.id, onUpdated,
      () => api.patch<BenefitDetailDto>(`/api/benefitapplications/${benefit.id}/target-year`, {
        concurrencyToken: token,
        targetProgramYear: selected === "default" ? null : Number(selected),
      }),
      "Target program year saved. Eligibility calculations were cleared.")
    setSaving(false)
    if (result.saved || result.reloaded) setEditing(false)
  }

  const cancel = () => setEditing(false)
  const displayed = editing ? choices : readback
  const active = displayed?.options.find(option => option.selected)
  const defaultYear = displayed?.options.find(option => option.isDefault)?.programYear ?? displayed?.effectiveProgramYear
  const pending = displayed?.options.find(option => option.programYear === (selection === "default"
    ? defaultYear : Number(selection)))?.pendingPrograms ?? active?.pendingPrograms ?? []

  return <Card>
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <IconCalendar className="size-5" />
          <div>
            <CardTitle className="text-base">Target calculation year</CardTitle>
            <CardDescription>Choose the server-published year used by income screening, readiness, calculations, and new grant authoring.</CardDescription>
          </div>
        </div>
        <EditableCardActions isEditing={editing} isSaving={saving} canEdit={editable && !loading && !error && readback != null}
          saveDisabled={!canSave} restorePanelFocus
          onEdit={() => { if (!editable || loading || error || !readback) return; setChoices(structuredClone(readback)); setSelected(readback.selectedProgramYear == null ? "default" : String(readback.selectedProgramYear)); setEditing(true) }}
          onCancel={cancel} onSave={save} />
      </div>
    </CardHeader>
    <CardContent className="space-y-3">
      {loading && <p className="text-sm text-muted-foreground">Loading published calculation years…</p>}
      {error && <p role="status" className="rounded-md border border-warning-border bg-warning p-3 text-sm text-warning-foreground">{error}</p>}
      {editing && !choiceCurrent && <p role="status">The selected year is unavailable or ambiguous. Your original selection is retained; cancel and reopen to use current choices.</p>}
      {displayed && <>
        <div className="max-w-md space-y-1.5">
          <label htmlFor={`target-program-year-${benefit.id}`} className="text-sm font-medium">Calculation year</label>
          <Select value={selection} disabled={!editing || saving || !editable || loading || !!error} onValueChange={setSelected}>
            <SelectTrigger id={`target-program-year-${benefit.id}`} aria-label="Target calculation year"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Server default (PY{defaultYear})</SelectItem>
              {selection !== "default" && !displayed.options.some(option => String(option.programYear) === selection) && <SelectItem value={selection} disabled>PY{selection} — unavailable</SelectItem>}
              {displayed.options.map((option, index) => <SelectItem key={`${option.programYear}/${index}`} value={String(option.programYear)} disabled={!option.isAvailable}>
                PY{option.programYear}{option.isDefault ? " (default)" : ""} — {statusLabel(option.status)}
              </SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-muted-foreground">
          Effective year: <span className="font-medium text-foreground">PY{displayed.effectiveProgramYear}</span>
          {displayed.isExplicitSelection ? " (selected)" : " (server default)"}.
          {displayed.status !== "available" && <span className="ml-1">Status: {statusLabel(displayed.status)}.</span>}
        </p>
        {pending.length > 0 && <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/20">
          Pending configuration for: {pending.join(", ")}. These programs are not treated as calculated or awardable until their selected-year settings are effective.
        </p>}
        {!editable && <p className="text-xs text-muted-foreground">Target-year selection is available only for an editable, grantless unified draft or versioned intake. Completed, historical, and financial records remain unchanged.</p>}
      </>}
    </CardContent>
  </Card>
}
